import { type FunctionComponent } from 'react';
import { type TrackingLinkFieldsFragment, type InspirationItemFieldsFragment } from '../../../__generated__/graphql-client-types';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import { TextButton } from '../../buttons';
import { LinkButton } from '../../buttons/link-button/link-button.component';
import ArrowNarrowRightIconIcon from '../../svgs/untitled-ui/arrow-narrow-right.svg';
import { HomeCarousel } from '../home-carousel/home-carousel.component';
import { TipsInspirationSlide } from './tips-inspiration-slide/tips-inspiration-slide.component';

export type PanelProps = {
	title: string;
	description: string;
	callToAction: TrackingLinkFieldsFragment;
};

const PanelSmall: FunctionComponent<PanelProps> = ({ title, description, callToAction }) => {
	const navigate = useNavigation();
	const { url, tracking } = callToAction;
	return (
		<div className={`flex flex-column items-center justify-center pa3 bg-theme-white theme-dark-grey w-100`} data-testid="panel-small">
			<h1 className="f3 tc lh-title ma0 w-100">{title}</h1>
			<p className="mt2 mb4 tc f5 w-100">{description}</p>
			<div className="flex flex-row justify-between items-center theme-black">
				<TextButton onClick={() => navigate(url)} color="grey-darker" analyticsHook={tracking}>
					<>
						View All
						<ArrowNarrowRightIconIcon className="ml2 v-mid" />
					</>
				</TextButton>
			</div>
		</div>
	);
};

const PanelLarge: FunctionComponent<PanelProps> = ({ title, description, callToAction }) => {
	const { url, tracking } = callToAction;
	return (
		<div
			className={`flex flex-column items-center justify-center mw5 pa3 bg-theme-primary-dark theme-white w-100 h-100`}
			data-testid="panel-large">
			<h1 className="f2 tl lh-title ma0 w-100">{title}</h1>
			<p className="mt2 mb4 tl f5 w-100">{description}</p>
			<div className={`flex flex-column items-start justify-center w-100`}>
				<div>
					<LinkButton url={url} size="SMALL" buttonStyle="SECONDARY" analyticsHook={tracking}>
						View All
					</LinkButton>
				</div>
			</div>
		</div>
	);
};

export type HomeTipsInspirationProps = {
	title: string;
	description: string;
	callToAction: TrackingLinkFieldsFragment;
	content: InspirationItemFieldsFragment[];
};

export const HomeTipsInspiration: FunctionComponent<HomeTipsInspirationProps> = ({ title, description, callToAction, content }) => {
	return (
		<section
			className="flex flex-row-ns justify-start-ns items-stretch-ns flex-column items-center justify-center w-100 overflow-hidden h-100 pl1-ns"
			data-testid="home-tips-inspiration">
			{/* Side Panel */}
			<div className="dn db-ns mr3 ml2 min-h-100">
				<PanelLarge title={title} description={description} callToAction={callToAction} />
			</div>
			<div className="db dn-ns w-100">
				<PanelSmall title={title} description={description} callToAction={callToAction} />
			</div>
			{/* Articles Carousel */}
			<HomeCarousel
				items={content}
				CardComponent={TipsInspirationSlide}
				cardMinWidth={18.5}
				sectionClassName="list ma0 pa0 flex flex-column justify-between w-100 overflow-hidden"
				rootMargin="0% 10px"
			/>
		</section>
	);
};
